import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { cloneDeep } from 'lodash';
import { GlobalSpinnerService } from 'src/app/shared/services/global-spinner.service';
import { finalize, switchMap } from 'rxjs';
import { Profile } from 'src/app/models';
import { EnumerationItem } from 'src/app/models/simple-enum-item.model';
import { ProfileService } from 'src/app/modules/profile/profile.service';
import { ApplicationContextService } from 'src/app/services/application-context.service';
import { AuthService } from 'src/app/services/auth.service';
import { Constants } from 'src/app/services/constants';
import { EnumerationService } from 'src/app/services/enumeration-service';
import { NotificationService } from 'src/app/services/notification.service';
import { SecurityCodeDialog } from 'src/app/shared/components';

@Component({
    selector: 'app-tpo-settings',
    templateUrl: './tpo-settings.component.html',
    styleUrls: ['./tpo-settings.component.scss'],
    standalone: false
})
export class TpoSettingsComponent implements OnInit {

  setting = {
    phone: null,
    areaCode: null,
    isTwoFactor: null,
    twoFactorMethod: 'Text' as 'Text' | 'Email'
  }
  currentPhone: string;
  mfaMethods: EnumerationItem[] = [];

  updatingPhoneNumber: boolean = false;
  private profile: Profile;

  constructor(
    private _applicationContextService: ApplicationContextService,
    private _notificationService: NotificationService,
    private readonly _enumsService: EnumerationService,
    private readonly _spinner: GlobalSpinnerService,
    private readonly _modalService: NgbModal,
    private readonly _authService: AuthService,
    private _service: ProfileService,
  ) { }

  ngOnInit(): void {
    this.mfaMethods = this._enumsService.mfaMethods;
    this._applicationContextService.context.subscribe(
      (result) => {
        if (!result.currentlyLoggedInUserProfile?.userProfile) {
          return;
        }

        this.profile = cloneDeep(result.currentlyLoggedInUserProfile);
        this.currentPhone = this.profile.userProfile.phoneNumber;
        this.setting.isTwoFactor = this.profile.userProfile.twoFactorEnabled;
        this.setting.twoFactorMethod = this.normalizeTwoFactorMethod(this.profile.userProfile.twoFactorMethod);
      }, ({ error }) => {
        this._notificationService.showError(
          error ? error.message : 'Unable to load data',
          'Error!'
        );
      }
    )

  }

  updatePhone() {
    this.updatingPhoneNumber = true;
    this._service.updatePhoneNumber(this.setting.phone, this.setting.areaCode)
      .pipe(finalize(() => this.updatingPhoneNumber = false))
      .subscribe({
        next: (res) => {
          const modalRef = this._modalService.open(SecurityCodeDialog, Constants.modalOptions.medium)
          modalRef.componentInstance.confirmOperation.subscribe((securityCode: string) => {

            modalRef.componentInstance.isSending = true;
            this._authService.confirmPhoneNumber(this.setting.phone, this.setting.areaCode, securityCode)
              .pipe(finalize(() => modalRef.componentInstance.isSending = false))
              .subscribe({
                next: () => {
                  this.currentPhone = this.setting.phone;
                  this._applicationContextService.updateLoggedInUserMfa(
                    this.setting.isTwoFactor,
                    this.currentPhone
                  );
                  modalRef.close();
                  this._notificationService.showSuccess(
                    'Phone successfuly updated',
                    'Success!'
                  );
                },
                error: (error) => {
                  if (error?.code === "InvalidToken") {
                    this._notificationService.showError(error.description, "Error");
                  } else {
                    this._notificationService.showError(error?.message || "Couldn't process the confirmation code", "Error");
                  }
                }
              })
          })
        },
        error: (error) => {
          this._notificationService.showError(
            error?.message || 'Unable to update phone number',
            'Profile Service'
          );
        }
      });
  }

  changeTwoFactor() {
    this._spinner.show();
    this._service.updateTwoFactor(this.setting.isTwoFactor)
      .pipe(finalize(() => this._spinner.hide()))
      .subscribe({
        next: (res) => {
          this._applicationContextService.updateLoggedInUserMfa(this.setting.isTwoFactor);
          this._notificationService.showSuccess(
            'Two factor authentication setting successfuly updated',
            'Success!'
          );
        },
        error: (error) => {
          this._notificationService.showError(
            error?.message || 'An error occurred updating two factor authentication setting',
            'Error!'
          );
        }
      })
  }

  changeTwoFactorMethod() {
    if (!this.profile || this.normalizeTwoFactorMethod(this.profile.userProfile.twoFactorMethod) === this.setting.twoFactorMethod) {
      return;
    }

    this._spinner.show();
    this._service.getUserProfile('0')
      .pipe(
        switchMap((profile) => this._service.saveUserProfile({
          userProfile: {
            ...profile.userProfile,
            twoFactorMethod: this.setting.twoFactorMethod
          }
        }))
      )
      .pipe(finalize(() => this._spinner.hide()))
      .subscribe({
        next: (savedProfile) => {
          const updatedUserProfile = savedProfile?.userProfile ?? {
            ...this.profile.userProfile,
            twoFactorMethod: this.setting.twoFactorMethod
          };
          this.profile = {
            ...this.profile,
            userProfile: cloneDeep(updatedUserProfile)
          };
          this._applicationContextService.updateUserProfile(updatedUserProfile);
          this._notificationService.showSuccess(
            'Default MFA method successfully updated',
            'Success!'
          );
        },
        error: (error) => {
          this.setting.twoFactorMethod = this.normalizeTwoFactorMethod(this.profile.userProfile.twoFactorMethod);
          this._notificationService.showError(
            error?.message || 'An error occurred updating the default MFA method',
            'Error!'
          );
        }
      });
  }

  private normalizeTwoFactorMethod(method: number | string | null | undefined): 'Text' | 'Email' {
    return method === 'Email' || method === 1 ? 'Email' : 'Text';
  }

  // TODO verify
  onNewPhoneNumberChanged = () => {
    if (!this.setting.phone && !this.currentPhone) {
      this.setting.isTwoFactor = false;
      this.changeTwoFactor();
    }
  }
}
